import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiEye,
  FiInbox,
  FiMoreVertical,
  FiPhoneCall,
  FiPhoneIncoming,
  FiPhoneOutgoing,
  FiRefreshCw,
  FiUserCheck,
  FiXCircle,
} from "react-icons/fi";
import AssignCallModal from "../components/calls/AssignCallModal";
import StartCallModal from "../components/calls/StartCallModal";
import Badge from "../components/common/Badge";
import Button from "../components/common/Button";
import EmptyState from "../components/common/EmptyState";
import RetryBlock from "../components/common/RetryBlock";
import SearchInput from "../components/common/SearchInput";
import Skeleton from "../components/common/Skeleton";
import TranscriptModal from "../components/modals/TranscriptModal";
import { useAuth } from "../context/AuthContext";
import useDebounce from "../hooks/useDebounce";
import useShellContext from "../layout/useShellContext";
import { getAiConfigs } from "../services/aiConfigApi";
import {
  assignCall,
  cancelCall,
  getAssignableCallingAgents,
  getCallDetail,
  getCallHistory,
  getCalls,
  getCallStats,
  retryCall,
} from "../services/callsApi";
import { startOutboundCall } from "../services/outboundApi";
import { syncRecordings as triggerRecordingSync } from "../services/recordingsApi";
import { downloadTranscript } from "../utils/callDisplay";
import { normalizeApiError } from "../utils/errors";
import {
  formatDateTime,
  formatDuration,
  formatNow,
  formatPhone,
} from "../utils/formatters";
import { DATE_RANGE_OPTIONS, resolveDateRangeFilter } from "../utils/dateRanges";
import { isAdminUser } from "../utils/roleAccess";

const CALLS_TAB = "calls";
const HISTORY_TAB = "history";
const PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const POLL_INTERVAL_MS = 15000;

const CALL_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "booked", label: "Booked" },
  { value: "dropped", label: "Dropped" },
  { value: "info", label: "Info" },
];
const CALL_STATUS_VALUES = new Set(
  CALL_STATUS_OPTIONS.map((option) => option.value).filter(Boolean)
);

const DURATION_OPTIONS = [
  { value: "", label: "All durations", min: null, max: null },
  { value: "lt10", label: "<10s", min: 0, max: 9 },
  { value: "10-30", label: "10-30s", min: 10, max: 30 },
  { value: "30-60", label: "30s-1m", min: 31, max: 60 },
  { value: "60-180", label: "1-3m", min: 61, max: 180 },
  { value: "gt180", label: "3m+", min: 181, max: null },
];
const DURATION_VALUES = new Set(
  DURATION_OPTIONS.map((option) => option.value).filter(Boolean)
);

const CANCELLABLE_STATUSES = new Set([
  "pending",
  "queued",
  "scheduled",
  "retry_scheduled",
  "calling",
  "ringing",
  "answered",
  "initiated",
  "in_progress",
  "assigned",
  "callback_required",
  "follow_up_pending",
]);

const RETRYABLE_STATUSES = new Set([
  "failed",
  "dropped",
  "busy",
  "no_answer",
  "cancelled",
]);

const STATUS_LABELS = {
  pending: "Pending",
  queued: "Queued",
  scheduled: "Scheduled",
  retry_scheduled: "Scheduled",
  calling: "Live",
  ringing: "Live",
  answered: "Live",
  initiated: "In Progress",
  in_progress: "In Progress",
  assigned: "Pending",
  callback_required: "Pending",
  follow_up_pending: "Pending",
  completed: "Success",
  booked: "Success",
  success: "Success",
  escalated: "Escalated",
  human_needed: "Human Needed",
  failed: "Failed",
  dropped: "Dropped",
  busy: "Dropped",
  no_answer: "Dropped",
  cancelled: "Cancelled",
  not_interested: "Failed",
};

const STATUS_VARIANTS = {
  pending: "info",
  queued: "info",
  scheduled: "info",
  retry_scheduled: "info",
  calling: "warning",
  ringing: "warning",
  answered: "warning",
  initiated: "warning",
  in_progress: "warning",
  assigned: "info",
  callback_required: "warning",
  follow_up_pending: "info",
  completed: "success",
  booked: "success",
  success: "success",
  escalated: "warning",
  human_needed: "warning",
  failed: "danger",
  dropped: "danger",
  busy: "danger",
  no_answer: "danger",
  cancelled: "muted",
  not_interested: "danger",
};

function getInitialCallsQuery(search = "") {
  const params = new URLSearchParams(search);
  const requestedTab = params.get("tab");
  const requestedStatus = params.get("status") || "";
  const requestedDuration = params.get("duration") || "";

  return {
    tab: [CALLS_TAB, HISTORY_TAB].includes(requestedTab)
      ? requestedTab
      : HISTORY_TAB,
    status: CALL_STATUS_VALUES.has(requestedStatus) ? requestedStatus : "",
    direction: params.get("direction") || "",
    range: params.get("range") || "",
    duration: DURATION_VALUES.has(requestedDuration) ? requestedDuration : "",
    line: params.get("line") || "",
    date_from: params.get("date_from") || "",
    date_to: params.get("date_to") || "",
  };
}

const EMPTY_CALL_FORM = {
  phone: "",
  name: "",
  reason: "",
  ai_config_id: "",
  voice_provider: "openai",
};

function getManualRecordingSyncRange() {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 30);

  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
}

function formatRecordingSyncSummary(summary = {}) {
  const found = Number(summary.found || 0);
  const uploaded = Number(summary.uploaded || 0);
  const updated = Number(summary.updated || 0);
  const skipped = Number(summary.skipped || 0);
  const failed = Number(summary.failed || 0);
  const matched = Number(summary.matched || 0);
  const parts = [
    `${found} found`,
    `${uploaded} uploaded`,
    `${updated} updated`,
    `${skipped} skipped`,
    `${matched} matched`,
  ];

  if (failed > 0) {
    parts.push(`${failed} failed`);
  }

  return `Recording sync complete: ${parts.join(", ")}.`;
}

function normalizeStatus(status) {
  return String(status || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function humanize(value) {
  const text = String(value || "").trim();

  if (!text) {
    return "--";
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getRowId(row = {}) {
  return row.call_id || row.id || row.call_uuid || "";
}

function getCallerName(row = {}) {
  return (
    row.name ||
    row.patient_name ||
    row.caller_name ||
    row.contact_name ||
    row.contact?.name ||
    "Unknown"
  );
}

function getPhone(row = {}) {
  return row.phone_number || row.phone || row.contact?.phone || "";
}

function getSource(row = {}) {
  return (
    row.source ||
    row.metadata?.source ||
    row.metadata?.origin ||
    row.campaign_name ||
    row.service_type ||
    "Dashboard"
  );
}

function getNotes(row = {}) {
  return (
    row.notes ||
    row.latest_note ||
    row.summary_preview ||
    row.summary?.summary ||
    row.summary ||
    row.metadata?.notes ||
    row.metadata?.reason ||
    ""
  );
}

function getStatusMeta(status) {
  const normalized = normalizeStatus(status);

  return {
    label: STATUS_LABELS[normalized] || humanize(normalized),
    variant: STATUS_VARIANTS[normalized] || "info",
  };
}

function getScriptStatus(row = {}) {
  const explicit = normalizeStatus(row.script_status);
  const status = normalizeStatus(row.status || row.call_status || row.booking_status);

  if (explicit === "escalated" || explicit === "escalation") {
    return { label: "Escalated", variant: "warning" };
  }

  if (
    explicit === "human_needed" ||
    explicit === "human_required" ||
    explicit === "needs_human"
  ) {
    return { label: "Human Needed", variant: "warning" };
  }

  if (explicit === "success" || ["completed", "booked", "success"].includes(status)) {
    return { label: "Success", variant: "success" };
  }

  if (["failed", "dropped", "busy", "no_answer", "cancelled", "not_interested"].includes(status)) {
    return { label: "Failed", variant: "danger" };
  }

  return { label: "Info", variant: "info" };
}

function getHistoryDate(row = {}) {
  return row.date_time || row.ended_at || row.started_at || row.created_at || row.updated_at;
}

function getLastContacted(row = {}) {
  return (
    row.last_contacted_at ||
    row.ended_at ||
    row.answered_at ||
    row.started_at ||
    row.updated_at ||
    null
  );
}

function getDurationSeconds(row = {}) {
  return Number(
    row.duration_seconds ??
      row.latest_attempt_duration_seconds ??
      row.latest_attempt?.duration_seconds ??
      row.recording_duration_seconds ??
      row.recording_duration ??
      0
  );
}

function formatCallDuration(row = {}) {
  const seconds = getDurationSeconds(row);

  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "0:00";
  }

  return formatDuration(seconds);
}

function formatClockDuration(seconds) {
  const rounded = Math.max(0, Math.round(Number(seconds || 0)));
  const minutes = Math.floor(rounded / 60);
  const remainder = rounded % 60;

  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function resolveDurationFilter(value = "") {
  const option = DURATION_OPTIONS.find((item) => item.value === value);

  return {
    duration_min: option?.min ?? null,
    duration_max: option?.max ?? null,
  };
}

function getAssignedAgentName(row = {}) {
  return (
    row.assigned_agent_name ||
    row.assignedAgent?.name ||
    row.assigned_agent?.name ||
    row.agent_name ||
    row.assigned_user_name ||
    ""
  );
}

function getInitials(name = "") {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return "UA";
  }

  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function normalizeLiveCallPrefill(prefill = {}) {
  return {
    phone: prefill.phone || prefill.phone_number || "",
    name:
      prefill.name ||
      prefill.patient_name ||
      prefill.caller_name ||
      prefill.contact_name ||
      "",
    reason: prefill.reason || prefill.summary || "follow-up",
    ai_config_id: prefill.ai_config_id || "",
    voice_provider: prefill.voice_provider || "openai",
  };
}

function validateCallForm(form) {
  const errors = {};
  // The ElevenLabs voice demo only needs a phone number.
  const isElevenDemo = (form.voice_provider || "openai") === "elevenlabs";

  if (!String(form.phone || "").trim()) {
    errors.phone = "Phone number is required.";
  }

  if (!isElevenDemo && !String(form.name || "").trim()) {
    errors.name = "Person name is required.";
  }

  if (!isElevenDemo && !String(form.reason || "").trim()) {
    errors.reason = "Reason is required.";
  }

  return errors;
}

function getPaginationItems(page, totalPages) {
  const pages = [];

  if (totalPages <= 7) {
    for (let index = 1; index <= totalPages; index += 1) {
      pages.push(index);
    }

    return pages;
  }

  if (page <= 4) {
    for (let index = 1; index <= 5; index += 1) {
      pages.push(index);
    }
    pages.push("end-ellipsis", totalPages);
    return pages;
  }

  if (page >= totalPages - 3) {
    pages.push(1, "start-ellipsis");
    for (let index = totalPages - 4; index <= totalPages; index += 1) {
      pages.push(index);
    }
    return pages;
  }

  pages.push(1, "start-ellipsis", page - 1, page, page + 1, "end-ellipsis", totalPages);

  return pages;
}

function Pagination({ meta = {}, onPageChange, pageSize, onPageSizeChange }) {
  const page = Math.max(1, Number(meta.page || 1));
  const limit = Number(meta.limit || pageSize || PAGE_SIZE);
  const total = Number(meta.total || meta.totalCount || meta.total_count || 0);
  const totalPages = Math.max(
    1,
    Number(meta.totalPages || meta.total_pages || Math.ceil(total / limit) || 1)
  );
  const firstResult = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastResult = total > 0 ? Math.min(page * limit, total) : 0;
  const pageItems = getPaginationItems(page, totalPages);

  return (
    <div className="calls-pagination">
      <div className="calls-pagination-summary">
        {total > 0 ? (
          <span>
            Showing {firstResult.toLocaleString()}-{lastResult.toLocaleString()} of{" "}
            {total.toLocaleString()}
          </span>
        ) : (
          <span>No results</span>
        )}
        <label className="calls-page-size">
          <span>Rows</span>
          <select
            value={limit}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="calls-pagination-controls" aria-label="Pagination">
        <Button
          className="compact-btn"
          disabled={page <= 1 || total === 0}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <div className="calls-pagination-pages">
          {pageItems.map((item) =>
            typeof item === "number" ? (
              <button
                key={item}
                type="button"
                className={`calls-page-number ${item === page ? "active" : ""}`.trim()}
                onClick={() => onPageChange(item)}
                aria-current={item === page ? "page" : undefined}
              >
                {item}
              </button>
            ) : (
              <span key={item} className="calls-page-ellipsis">
                ...
              </span>
            )
          )}
        </div>
        <Button
          className="compact-btn"
          disabled={page >= totalPages || total === 0}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function StatsCards({ stats = {}, loading }) {
  const totalCalls = Number(stats.totalCalls || stats.total_calls || 0);
  const inboundCalls = Number(stats.inboundCalls || stats.inbound_calls || 0);
  const outboundCalls = Number(stats.outboundCalls || stats.outbound_calls || 0);
  const totalDurationSeconds = Number(
    stats.totalDurationSeconds || stats.total_duration_seconds || 0
  );
  const connectedCalls = Number(
    stats.connectedCalls ||
      stats.connected_calls ||
      stats.answeredCalls ||
      stats.answered_calls ||
      0
  );

  const cards = [
    {
      label: "Total Calls",
      value: totalCalls.toLocaleString(),
      context: `${totalCalls.toLocaleString()} total`,
      icon: FiPhoneCall,
      tone: "blue",
    },
    {
      label: "Inbound Calls",
      value: inboundCalls.toLocaleString(),
      context: `${inboundCalls.toLocaleString()} received`,
      icon: FiPhoneIncoming,
      tone: "cyan",
    },
    {
      label: "Outbound Calls",
      value: outboundCalls.toLocaleString(),
      context: `${outboundCalls.toLocaleString()} placed`,
      icon: FiPhoneOutgoing,
      tone: "amber",
    },
    {
      label: "Duration",
      value: formatClockDuration(totalDurationSeconds),
      context: "Total talk time",
      icon: FiClock,
      tone: "teal",
    },
    {
      label: "Connected Calls",
      value: connectedCalls.toLocaleString(),
      context: "Answered & connected",
      icon: FiCheckCircle,
      tone: "green",
    },
  ];

  return (
    <div className="calls-kpi-grid">
      {cards.map((card) => {
        const Icon = card.icon;

        return (
          <div
            key={card.label}
            className={`calls-kpi-card ${card.tone}`.trim()}
          >
            <span className="calls-kpi-icon">
              <Icon />
            </span>
            <div className="calls-kpi-copy">
              <div className="calls-kpi-label">{card.label}</div>
              {loading ? (
                <Skeleton className="mt-3 h-8 w-20" />
              ) : (
                <>
                  <div className="calls-kpi-value">{card.value}</div>
                  <div className="calls-kpi-context">{card.context}</div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = getStatusMeta(status);

  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function ScriptStatusBadge({ row }) {
  const meta = getScriptStatus(row);

  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function DirectionBadge({ row }) {
  const direction = normalizeStatus(row.direction || row.call_direction);

  if (direction === "inbound") {
    return <Badge variant="inbound">{"< Inbound"}</Badge>;
  }

  if (direction === "outbound") {
    return <Badge variant="outbound">{"> Outbound"}</Badge>;
  }

  return <span className="text-slate-400">--</span>;
}

function AgentChip({ row }) {
  const agentName = getAssignedAgentName(row);

  if (!agentName) {
    return (
      <span className="agent-chip unassigned">
        <span className="agent-dot" />
        Unassigned
      </span>
    );
  }

  return (
    <span className="agent-chip assigned" title={agentName}>
      <span className="agent-avatar">{getInitials(agentName)}</span>
      <span className="agent-name">{agentName}</span>
    </span>
  );
}

function RowActionMenu({ label = "Row actions", children }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const calculatePosition = useCallback((trigger) => {
    if (!trigger) {
      return null;
    }

    const rect = trigger.getBoundingClientRect();
    if (
      rect.width <= 0 ||
      rect.height <= 0 ||
      rect.bottom < 0 ||
      rect.top > window.innerHeight ||
      rect.right < 0 ||
      rect.left > window.innerWidth
    ) {
      return null;
    }

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const topElement = document.elementFromPoint(centerX, centerY);
    if (topElement && !trigger.contains(topElement)) {
      return null;
    }

    const panelWidth = panelRef.current?.offsetWidth || 160;
    const panelHeight = panelRef.current?.offsetHeight || 112;
    const gap = 6;
    const viewportPadding = 8;
    let top = rect.bottom + gap;

    if (top + panelHeight > window.innerHeight - viewportPadding) {
      top = Math.max(viewportPadding, rect.top - panelHeight - gap);
    }

    const left = Math.max(
      viewportPadding,
      Math.min(rect.right - panelWidth, window.innerWidth - panelWidth - viewportPadding)
    );

    return { top, left };
  }, []);

  const updatePosition = useCallback(() => {
    const nextPosition = calculatePosition(triggerRef.current);
    if (!nextPosition) {
      setOpen(false);
      return;
    }
    setPosition(nextPosition);
  }, [calculatePosition]);

  useLayoutEffect(() => {
    if (open) {
      updatePosition();
    }
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    window.addEventListener("scroll", updatePosition, { capture: true, passive: true });
    window.addEventListener("resize", updatePosition);
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("scroll", updatePosition, { capture: true });
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, updatePosition]);

  const panel = open
    ? createPortal(
        <div
          ref={panelRef}
          className="row-action-menu-panel row-action-menu-panel-portal"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
          role="menu"
          onClick={(event) => {
            const target = event.target;
            if (target instanceof HTMLElement && target.closest("button:not(:disabled), a")) {
              setOpen(false);
            }
          }}
        >
          {children}
        </div>,
        document.body
      )
    : null;

  return (
    <span className="row-action-menu">
      <button
        ref={triggerRef}
        type="button"
        className="row-action-menu-trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          if (open) {
            setOpen(false);
            return;
          }

          const nextPosition = calculatePosition(event.currentTarget);
          if (nextPosition) {
            setPosition(nextPosition);
            setOpen(true);
          }
        }}
      >
        <FiMoreVertical />
      </button>
      {panel}
    </span>
  );
}

export default function CallsPage() {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { showToast, registerPageControls, resetPageControls } = useShellContext();
  const location = useLocation();
  const initialQuery = useMemo(() => getInitialCallsQuery(location.search), [location.search]);
  const mountedRef = useRef(false);
  const showToastRef = useRef(showToast);

  const [activeTab, setActiveTab] = useState(initialQuery.tab);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 350);
  const [statusFilter, setStatusFilter] = useState(initialQuery.status);
  const [directionFilter, setDirectionFilter] = useState(initialQuery.direction);
  const [dateRange, setDateRange] = useState(initialQuery.range);
  const [dateFrom, setDateFrom] = useState(initialQuery.date_from);
  const [dateTo, setDateTo] = useState(initialQuery.date_to);
  const [durationFilter, setDurationFilter] = useState(initialQuery.duration);
  const [lineFilter, setLineFilter] = useState(initialQuery.line);
  const dateFilters = useMemo(
    () => resolveDateRangeFilter(dateRange, dateFrom, dateTo),
    [dateRange, dateFrom, dateTo]
  );
  const durationFilters = useMemo(
    () => resolveDurationFilter(durationFilter),
    [durationFilter]
  );
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);

  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);

  const [callsData, setCallsData] = useState({ items: [], meta: {} });
  const [callsLoading, setCallsLoading] = useState(true);
  const [callsError, setCallsError] = useState("");
  const [callsPage, setCallsPage] = useState(1);
  const [callsPageSize, setCallsPageSize] = useState(PAGE_SIZE);

  const [historyData, setHistoryData] = useState({ items: [], meta: {} });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(PAGE_SIZE);

  const [busyAction, setBusyAction] = useState("");
  const [recordingSyncBusy, setRecordingSyncBusy] = useState(false);
  const [detailLoadingId, setDetailLoadingId] = useState("");
  const [transcriptCall, setTranscriptCall] = useState(null);

  const [assignTarget, setAssignTarget] = useState(null);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [callingAgents, setCallingAgents] = useState([]);
  const [callingAgentsLoading, setCallingAgentsLoading] = useState(false);
  const [callingAgentsError, setCallingAgentsError] = useState("");

  const [runtimeConfigs, setRuntimeConfigs] = useState([]);
  const [runtimeConfigsLoading, setRuntimeConfigsLoading] = useState(false);
  const [runtimeConfigsError, setRuntimeConfigsError] = useState("");
  const [callModal, setCallModal] = useState({
    open: false,
    form: EMPTY_CALL_FORM,
    busy: false,
    errors: {},
    submitError: "",
    successData: null,
  });

  const activeRuntimeConfigs = useMemo(
    () =>
      runtimeConfigs.filter(
        (config) => String(config.status || "").toUpperCase() === "ACTIVE"
      ),
    [runtimeConfigs]
  );
  const sharedFilterParams = useMemo(
    () => ({
      search: debouncedSearch,
      status: statusFilter || undefined,
      direction: directionFilter || undefined,
      date_from: dateFilters.date_from,
      date_to: dateFilters.date_to,
      duration_min: durationFilters.duration_min,
      duration_max: durationFilters.duration_max,
      line: lineFilter.trim() || undefined,
    }),
    [
      dateFilters.date_from,
      dateFilters.date_to,
      debouncedSearch,
      directionFilter,
      durationFilters.duration_max,
      durationFilters.duration_min,
      lineFilter,
      statusFilter,
    ]
  );

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  useEffect(() => {
    setActiveTab(initialQuery.tab);
    setStatusFilter(initialQuery.status);
    setDirectionFilter(initialQuery.direction);
    setDateRange(initialQuery.range);
    setDateFrom(initialQuery.date_from);
    setDateTo(initialQuery.date_to);
    setDurationFilter(initialQuery.duration);
    setLineFilter(initialQuery.line);
    setHistoryPage(1);
  }, [initialQuery]);

  const loadStats = useCallback(
    async ({ silent = false, signal } = {}) => {
      if (!silent) {
        setStatsLoading(true);
      }

      try {
        const nextStats = await getCallStats(
          sharedFilterParams,
          { signal }
        );

        if (!mountedRef.current) {
          return;
        }

        setStats(nextStats || {});
      } catch (error) {
        const normalized = normalizeApiError(error);

        if (!normalized.isAbort && !silent) {
          showToastRef.current(
            normalized.message || "Failed to load call stats.",
            true,
            5000
          );
        }
      } finally {
        if (mountedRef.current && !silent) {
          setStatsLoading(false);
        }
      }
    },
    [sharedFilterParams]
  );

  const loadCalls = useCallback(
    async ({ silent = false, signal } = {}) => {
      if (!silent) {
        setCallsLoading(true);
      }
      setCallsError("");

      try {
        const data = await getCalls(
          {
            ...sharedFilterParams,
            page: callsPage,
            limit: callsPageSize,
          },
          { signal }
        );

        if (!mountedRef.current) {
          return;
        }

        setCallsData(data);
        setLastUpdated(formatNow());
      } catch (error) {
        const normalized = normalizeApiError(error);

        if (!normalized.isAbort) {
          setCallsError(normalized.message || "Failed to load calls.");
        }
      } finally {
        if (mountedRef.current && !silent) {
          setCallsLoading(false);
        }
      }
    },
    [callsPage, callsPageSize, sharedFilterParams]
  );

  const loadHistory = useCallback(
    async ({ silent = false, signal } = {}) => {
      if (!silent) {
        setHistoryLoading(true);
      }
      setHistoryError("");

      try {
        const data = await getCallHistory(
          {
            ...sharedFilterParams,
            page: historyPage,
            limit: historyPageSize,
          },
          { signal }
        );

        if (!mountedRef.current) {
          return;
        }

        setHistoryData(data);
        setLastUpdated(formatNow());
      } catch (error) {
        const normalized = normalizeApiError(error);

        if (!normalized.isAbort) {
          setHistoryError(normalized.message || "Failed to load call history.");
        }
      } finally {
        if (mountedRef.current && !silent) {
          setHistoryLoading(false);
        }
      }
    },
    [
      historyPage,
      historyPageSize,
      sharedFilterParams,
    ]
  );

  const refreshCurrentView = useCallback(
    async ({ silent = false } = {}) => {
      setShellRefreshing(!silent);
      const loadActiveTab =
        activeTab === CALLS_TAB
          ? loadCalls
          : loadHistory;
      try {
        await Promise.all([
          loadStats({ silent }),
          loadActiveTab({ silent }),
        ]);
      } finally {
        if (mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [activeTab, loadCalls, loadHistory, loadStats]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setCallsPage(1);
    setHistoryPage(1);
  }, [
    debouncedSearch,
    dateFilters.date_from,
    dateFilters.date_to,
    directionFilter,
    durationFilter,
    lineFilter,
    statusFilter,
  ]);

  useEffect(() => {
    const controller = new AbortController();

    loadStats({ signal: controller.signal });
    if (activeTab === CALLS_TAB) {
      loadCalls({ signal: controller.signal });
    } else {
      loadHistory({ signal: controller.signal });
    }

    return () => controller.abort();
  }, [activeTab, loadCalls, loadHistory, loadStats]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refreshCurrentView({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [refreshCurrentView]);

  useEffect(() => {
    registerPageControls({
      onRefresh: () => refreshCurrentView(),
      refreshEvery: `${Math.round(POLL_INTERVAL_MS / 1000)}s`,
      lastUpdated,
      isRefreshing: shellRefreshing,
    });

    return () => resetPageControls();
  }, [
    lastUpdated,
    refreshCurrentView,
    registerPageControls,
    resetPageControls,
    shellRefreshing,
  ]);

  useEffect(() => {
    if (!isAdmin) {
      return undefined;
    }

    const controller = new AbortController();
    setCallingAgentsLoading(true);
    setCallingAgentsError("");

    getAssignableCallingAgents({ signal: controller.signal })
      .then((agents) => {
        if (mountedRef.current) {
          setCallingAgents(Array.isArray(agents) ? agents : []);
        }
      })
      .catch((error) => {
        const normalized = normalizeApiError(error);
        if (mountedRef.current && !normalized.isAbort) {
          setCallingAgentsError(normalized.message || "Unable to load agents.");
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setCallingAgentsLoading(false);
        }
      });

    return () => controller.abort();
  }, [isAdmin]);

  useEffect(() => {
    const controller = new AbortController();
    setRuntimeConfigsLoading(true);
    setRuntimeConfigsError("");

    getAiConfigs({ page: 1, limit: 100 }, { signal: controller.signal })
      .then((data) => {
        if (mountedRef.current) {
          setRuntimeConfigs(data.items || []);
        }
      })
      .catch((error) => {
        const normalized = normalizeApiError(error);
        if (mountedRef.current && !normalized.isAbort) {
          setRuntimeConfigsError(normalized.message || "Unable to load runtime configs.");
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setRuntimeConfigsLoading(false);
        }
      });

    return () => controller.abort();
  }, []);

  function openCallModal(prefill = {}) {
    setCallModal({
      open: true,
      form: normalizeLiveCallPrefill(prefill),
      busy: false,
      errors: {},
      submitError: "",
      successData: null,
    });
  }

  function closeCallModal() {
    if (callModal.busy) {
      return;
    }

    setCallModal((current) => ({ ...current, open: false }));
  }

  function updateCallForm(field, value) {
    setCallModal((current) => ({
      ...current,
      form: {
        ...current.form,
        [field]: value,
      },
      errors: {
        ...current.errors,
        [field]: "",
      },
      submitError: "",
    }));
  }

  async function submitCallModal(event) {
    event.preventDefault();
    const errors = validateCallForm(callModal.form);

    if (Object.keys(errors).length) {
      setCallModal((current) => ({ ...current, errors }));
      return;
    }

    setCallModal((current) => ({ ...current, busy: true, submitError: "" }));

    try {
      const result = await startOutboundCall(callModal.form);
      setCallModal((current) => ({
        ...current,
        busy: false,
        successData: result,
      }));
      showToast("Call start accepted.", false, 4000);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      setCallModal((current) => ({
        ...current,
        busy: false,
        submitError: normalized.message || "Unable to start call.",
      }));
    }
  }

  async function handleCancel(row) {
    const rowId = getRowId(row);
    if (!rowId) {
      return;
    }

    setBusyAction(`cancel-${rowId}`);
    try {
      await cancelCall(rowId, { reason: "operator_cancel" });
      showToast("Call cancelled.", false, 4000);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to cancel call.", true, 5000);
    } finally {
      setBusyAction("");
    }
  }

  async function handleRetry(row) {
    const rowId = getRowId(row);
    if (!rowId) {
      return;
    }

    setBusyAction(`retry-${rowId}`);
    try {
      await retryCall(rowId, { reason: "manual_retry" });
      showToast("Retry scheduled.", false, 4000);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to retry call.", true, 5000);
    } finally {
      setBusyAction("");
    }
  }

  async function handleView(row) {
    const rowId = getRowId(row);
    if (!rowId) {
      setTranscriptCall(row);
      return;
    }

    setDetailLoadingId(String(rowId));
    try {
      const detail = await getCallDetail(rowId);
      setTranscriptCall({
        ...row,
        ...detail,
        caller_name: detail.caller_name || getCallerName(row),
        phone_number: detail.phone_number || getPhone(row),
        created_at: detail.created_at || getHistoryDate(row),
        __modalRequestKey: `${rowId}-${Date.now()}`,
      });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to load call details.", true, 5000);
    } finally {
      setDetailLoadingId("");
    }
  }

  async function handleAssign(payload) {
    if (!assignTarget) {
      return;
    }

    const rowId = getRowId(assignTarget);
    setAssignmentBusy(true);

    try {
      await assignCall(rowId, payload);
      showToast("Call assigned successfully.", false, 4000);
      setAssignTarget(null);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to assign call.", true, 5000);
    } finally {
      setAssignmentBusy(false);
    }
  }

  async function handleSyncRecordings() {
    setRecordingSyncBusy(true);
    try {
      const summary = await triggerRecordingSync(getManualRecordingSyncRange());
      const failed = Number(summary?.failed || 0) > 0;
      showToast(
        formatRecordingSyncSummary(summary),
        failed,
        failed ? 7000 : 5000
      );
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to sync recordings.", true, 5000);
    } finally {
      setRecordingSyncBusy(false);
    }
  }

  const callsRows = callsData.items || [];
  const historyRows = historyData.items || [];
  const callsMetaTotal =
    callsData.meta?.total ?? callsData.meta?.totalCount ?? callsData.meta?.total_count;
  const historyMetaTotal =
    historyData.meta?.total ?? historyData.meta?.totalCount ?? historyData.meta?.total_count;
  const activeCallsCount = Number(
    callsMetaTotal ?? stats.activeCalls ?? stats.active_calls ?? 0
  );
  const historyCallsCount = Number(
    historyMetaTotal ??
      stats.totalCalls ??
      stats.total_calls ??
      0
  );
  const tabItems = [
    { id: CALLS_TAB, label: "Calls", count: statsLoading ? "..." : activeCallsCount.toLocaleString() },
    { id: HISTORY_TAB, label: "History", count: statsLoading ? "..." : historyCallsCount.toLocaleString() },
  ];
  const activeFilterCount = [
    searchValue.trim(),
    statusFilter,
    directionFilter,
    dateRange,
    dateFrom,
    dateTo,
    durationFilter,
    lineFilter.trim(),
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearchValue("");
    setStatusFilter("");
    setDirectionFilter("");
    setDateRange("");
    setDateFrom("");
    setDateTo("");
    setDurationFilter("");
    setLineFilter("");
    setCallsPage(1);
    setHistoryPage(1);
  };

  const changeCallsPageSize = (nextPageSize) => {
    setCallsPageSize(nextPageSize);
    setCallsPage(1);
  };

  const changeHistoryPageSize = (nextPageSize) => {
    setHistoryPageSize(nextPageSize);
    setHistoryPage(1);
  };

  return (
    <div className="space-y-7 calls-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-950">Calls</h1>
          <p className="mt-2 text-sm text-slate-500">
            Manage active calls and review completed call history.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin ? (
            <Button
              variant="secondary"
              className="compact-btn"
              loading={recordingSyncBusy}
              onClick={handleSyncRecordings}
            >
              <FiDownload />
              <span>Sync Recordings</span>
            </Button>
          ) : null}
          <Button variant="blue" onClick={() => openCallModal()}>
            <FiPhoneCall />
            <span>Live Call</span>
          </Button>
        </div>
      </div>

      <div className="calls-filter-panel">
        <div className="calls-filter-bar">
          <SearchInput
            value={searchValue}
            onChange={(event) => {
              setSearchValue(event.target.value);
              setCallsPage(1);
              setHistoryPage(1);
            }}
            placeholder="Search calls, names, summaries..."
            className="calls-filter-search"
          />
          <select
            className="filter-select"
            value={dateRange}
            onChange={(event) => {
              setDateRange(event.target.value);
              setCallsPage(1);
              setHistoryPage(1);
            }}
            aria-label="Filter calls by time range"
          >
            {DATE_RANGE_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="filter-select"
            value={directionFilter}
            onChange={(event) => {
              setDirectionFilter(event.target.value);
              setCallsPage(1);
              setHistoryPage(1);
            }}
            aria-label="Filter calls by direction"
          >
            <option value="">All directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setCallsPage(1);
              setHistoryPage(1);
            }}
            aria-label="Filter calls by status"
          >
            {CALL_STATUS_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="filter-select"
            value={durationFilter}
            onChange={(event) => {
              setDurationFilter(event.target.value);
              setCallsPage(1);
              setHistoryPage(1);
            }}
            aria-label="Filter calls by duration"
          >
            {DURATION_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="filter-text-input calls-line-filter"
            value={lineFilter}
            onChange={(event) => {
              setLineFilter(event.target.value);
              setCallsPage(1);
              setHistoryPage(1);
            }}
            placeholder="Number / line"
            aria-label="Filter calls by number or line"
          />
          {dateRange === "custom" ? (
            <div className="custom-date-range calls-custom-range">
              <input
                type="date"
                className="filter-date-input"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setCallsPage(1);
                  setHistoryPage(1);
                }}
                aria-label="Custom date from"
              />
              <input
                type="date"
                className="filter-date-input"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setCallsPage(1);
                  setHistoryPage(1);
                }}
                aria-label="Custom date to"
              />
            </div>
          ) : null}
          <button
            type="button"
            className="calls-clear-filters"
            onClick={clearFilters}
          >
            {activeFilterCount ? `Clear (${activeFilterCount})` : "Clear"}
          </button>
        </div>
      </div>

      <StatsCards stats={stats} loading={statsLoading} />

      <div className="calls-tabs-filter-shell">
        <div className="calls-tab-list">
          {tabItems.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`calls-tab-button ${
                activeTab === tab.id
                  ? "active"
                  : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.label}</span>
              <span className="calls-tab-count">
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="calls-tabs-actions">
          <Button
            className="compact-btn"
            disabled={shellRefreshing}
            onClick={() => refreshCurrentView()}
          >
            <FiRefreshCw />
            <span>{shellRefreshing ? "Refreshing..." : "Refresh"}</span>
          </Button>
        </div>
      </div>

      {activeTab === CALLS_TAB ? (
        <section className="space-y-4">
          {callsError ? (
            <RetryBlock message={callsError} onRetry={() => loadCalls()} />
          ) : null}

          <div className="calls-table-card">
            <div className="calls-table-scroll">
              <table className="calls-data-table calls-active-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone Number</th>
                    <th>Source</th>
                    <th>Scheduled Date & Time</th>
                    <th>Last Contacted</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th className="sticky-actions-head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {callsLoading ? (
                    Array.from({ length: 4 }).map((_, index) => (
                      <tr key={`calls-loading-${index}`}>
                        <td colSpan={8}>
                          <Skeleton className="h-7 w-full" />
                        </td>
                      </tr>
                    ))
                  ) : callsRows.length ? (
                    callsRows.map((row) => {
                      const rowId = getRowId(row);
                      const status = normalizeStatus(row.status || row.call_status);
                      const canCancel = CANCELLABLE_STATUSES.has(status);
                      const canRetry = RETRYABLE_STATUSES.has(status);

                      return (
                        <tr key={rowId || row.call_uuid}>
                          <td className="font-medium text-slate-950">
                            {getCallerName(row)}
                          </td>
                          <td>
                            {formatPhone(getPhone(row))}
                          </td>
                          <td>{getSource(row)}</td>
                          <td>
                            {formatDateTime(row.scheduled_at || row.scheduled_time)}
                          </td>
                          <td>
                            {formatDateTime(getLastContacted(row))}
                          </td>
                          <td>
                            <StatusBadge status={status} />
                          </td>
                          <td className="calls-notes-cell">
                            <span className="line-clamp-2">{getNotes(row) || "--"}</span>
                          </td>
                          <td className="sticky-actions-cell">
                            {canCancel || canRetry ? (
                              <RowActionMenu label={`Actions for ${getCallerName(row)}`}>
                                {canCancel ? (
                                  <Button
                                    variant="danger"
                                    className="compact-btn"
                                    disabled={busyAction === `cancel-${rowId}`}
                                    onClick={() => handleCancel(row)}
                                  >
                                    <FiXCircle />
                                    <span>
                                      {busyAction === `cancel-${rowId}` ? "Cancelling..." : "Cancel"}
                                    </span>
                                  </Button>
                                ) : null}
                                {canRetry ? (
                                  <Button
                                    className="compact-btn"
                                    disabled={busyAction === `retry-${rowId}`}
                                    onClick={() => handleRetry(row)}
                                  >
                                    <FiRefreshCw />
                                    <span>
                                      {busyAction === `retry-${rowId}` ? "Retrying..." : "Retry"}
                                    </span>
                                  </Button>
                                ) : null}
                              </RowActionMenu>
                            ) : (
                              <span className="text-sm text-slate-400">--</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="calls-empty-cell" colSpan={8}>
                        <EmptyState
                          icon={<FiInbox />}
                          title="No active calls right now"
                          subtitle="Queued, scheduled, live, pending, and in-progress calls will appear here. Use History to review completed interactions."
                          actionLabel="View History"
                          onAction={() => setActiveTab(HISTORY_TAB)}
                        >
                          <button
                            type="button"
                            className="btn btn-secondary compact-btn"
                            onClick={() => openCallModal()}
                          >
                            Schedule a Call
                          </button>
                        </EmptyState>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            meta={callsData.meta}
            pageSize={callsPageSize}
            onPageChange={setCallsPage}
            onPageSizeChange={changeCallsPageSize}
          />
        </section>
      ) : (
        <section className="space-y-4">
          {historyError ? (
            <RetryBlock message={historyError} onRetry={() => loadHistory()} />
          ) : null}

          <div className="calls-table-card">
            <div className="calls-table-scroll">
              <table className="calls-data-table calls-history-table">
                <thead>
                  <tr>
                    <th>Date/Time</th>
                    <th>Name</th>
                    <th>Phone Number</th>
                    <th>Direction</th>
                    <th>Duration</th>
                    <th>AI Status</th>
                    <th>Agent</th>
                    <th className="sticky-actions-head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    Array.from({ length: 4 }).map((_, index) => (
                      <tr key={`history-loading-${index}`}>
                        <td colSpan={8}>
                          <Skeleton className="h-7 w-full" />
                        </td>
                      </tr>
                    ))
                  ) : historyRows.length ? (
                    historyRows.map((row) => {
                      const rowId = getRowId(row);

                      return (
                        <tr key={rowId || row.call_uuid}>
                          <td>
                            {formatDateTime(getHistoryDate(row))}
                          </td>
                          <td className="font-medium text-slate-950">
                            {getCallerName(row)}
                          </td>
                          <td>
                            {formatPhone(getPhone(row))}
                          </td>
                          <td>
                            <DirectionBadge row={row} />
                          </td>
                          <td>
                            {formatCallDuration(row)}
                          </td>
                          <td>
                            <ScriptStatusBadge row={row} />
                          </td>
                          <td>
                            <AgentChip row={row} />
                          </td>
                          <td className="sticky-actions-cell">
                            <RowActionMenu label={`Actions for ${getCallerName(row)}`}>
                              {isAdmin ? (
                                <Button
                                  className="compact-btn"
                                  onClick={() => setAssignTarget(row)}
                                >
                                  <FiUserCheck />
                                  <span>Assign</span>
                                </Button>
                              ) : null}
                              <Button
                                className="compact-btn"
                                disabled={detailLoadingId === String(rowId)}
                                onClick={() => handleView(row)}
                              >
                                <FiEye />
                                <span>
                                  {detailLoadingId === String(rowId) ? "Loading..." : "View"}
                                </span>
                              </Button>
                            </RowActionMenu>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="calls-empty-cell" colSpan={8}>
                        <EmptyState
                          title="No calls match your filters"
                          subtitle="Try clearing filters or changing the date range to see more call history."
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            meta={historyData.meta}
            pageSize={historyPageSize}
            onPageChange={setHistoryPage}
            onPageSizeChange={changeHistoryPageSize}
          />
        </section>
      )}

      <StartCallModal
        open={callModal.open}
        title="Live Call"
        form={callModal.form}
        busy={callModal.busy}
        isPhoneEditable
        fieldErrors={callModal.errors}
        submitError={callModal.submitError}
        successData={callModal.successData}
        runtimeConfigs={activeRuntimeConfigs}
        runtimeConfigsLoading={runtimeConfigsLoading}
        runtimeConfigsError={runtimeConfigsError}
        onChange={updateCallForm}
        onSubmit={submitCallModal}
        onClose={closeCallModal}
      />

      <TranscriptModal
        open={Boolean(transcriptCall)}
        call={transcriptCall}
        showCallMetadata
        onDownloadTranscript={downloadTranscript}
        onClose={() => setTranscriptCall(null)}
        onCallAgain={(phone, name) => {
          setTranscriptCall(null);
          openCallModal({
            phone,
            name,
            reason: "follow-up",
          });
        }}
      />

      <AssignCallModal
        open={Boolean(assignTarget)}
        busy={assignmentBusy}
        agents={callingAgents}
        agentsLoading={callingAgentsLoading}
        agentsError={callingAgentsError}
        currentAgentId={assignTarget?.assigned_agent?.agent_id || assignTarget?.agent_id}
        callCount={1}
        onClose={() => {
          if (!assignmentBusy) {
            setAssignTarget(null);
          }
        }}
        onSubmit={handleAssign}
      />
    </div>
  );
}
